import type { Project, ProjectUpdateRequest } from "@/domain/project";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";
import { anyOf, byUuid, viewSlot, type ViewRules, type ViewShape } from "../cache/listViews";

export type ProjectPatch = Partial<Project>;

export interface BeneficiaryProjectsPage {
  projects: Project[];
  total_count: number;
  totalPages: number;
}

function isProject(value: unknown): value is Project {
  return isObject(value) && typeof value.uuid === "string" && typeof value.title === "string";
}

const projectsPage: ViewShape<BeneficiaryProjectsPage, Project, string> = {
  isPage: (value): value is BeneficiaryProjectsPage =>
    isObject(value) && Array.isArray(value.projects) && typeof value.total_count === "number",
  records: (page) => page.projects,
  withRecords: (page, projects, countDelta) => ({
    ...page,
    projects,
    total_count: page.total_count + countDelta,
  }),
  idOf: (project) => project.uuid,
  total: (page) => page.total_count,
};

const merge = (project: Project, patch: ProjectPatch): Project => ({ ...project, ...patch });

export const projectRules: ViewRules<Project> = {
  accepts: { status: anyOf((project) => project.status) },
  narrowing: ["search", "beneficiary_uuid", "fiscal_quota_uuid", "subtype", "fiscal_year"],
  order: { fiscal_year: (project) => project.fiscal_year },
  tieBreak: byUuid("asc", (project) => project.uuid),
};

export const projectCache = defineEntityCache<string, ProjectPatch, Project>({
  entity: InvalidationEntity.PROJECT,
  row: merge,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.projects.beneficiaryProjectsAll(orgId),
      shape: projectsPage,
      merge,
      parse: queryKeys.projects.parseBeneficiaryProjectsList,
      rules: projectRules,
    }),
    detailSlot({
      prefix: queryKeys.projects.details(orgId),
      isRecord: isProject,
      idOf: projectsPage.idOf,
      merge,
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.projects.detail(orgId, uuid),
    queryKeys.signed.projectPosters(orgId, uuid),
    queryKeys.signed.projectPoster(orgId, uuid),
  ],
});

/** The update as the project shows it: closing or reopening also moves the deprecated status. */
export function toProjectPatch(data: ProjectUpdateRequest): ProjectPatch {
  if (data.state === undefined) return data;
  return { ...data, status: data.state === "closed" ? "inactive" : "active" };
}
